/**
 * AffiliateResetPassword — final step of the portal's password reset.
 *
 * The verified OTP is replayed with the new password: the token alone does not
 * authorise the change, so a visitor who skipped the code step is sent back.
 * The length rule matches the affiliate application rather than the member
 * password criteria — a reset must not demand more than registration accepts.
 */
import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import affiliateService from '@wlb/shared/api/affiliate/affiliateService';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import {
  validateAffiliatePassword,
  validatePasswordMatch,
} from '@wlb/shared/utils/validation';
import { FormError } from '../components/ui/FormError';
import { PasswordInput } from '../components/ui/PasswordInput';
import { AffiliatePortalLayout } from '../features/affiliate/AffiliatePortalLayout';
import type { AffiliatePasswordResetState } from '../features/affiliate/affiliateFlowState';
import { AFFILIATE_ROUTES } from '../features/affiliate/affiliateRoutes';
import '../components/layout/authFlow.css';
import '../features/affiliate/affiliatePortal.css';

export function AffiliateResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as AffiliatePasswordResetState | null;
  const email = state?.email ?? '';
  const passwordResetToken = state?.passwordResetToken ?? '';
  const otpCode = state?.otpCode ?? '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!email || !passwordResetToken || !otpCode) {
    return <Navigate to={AFFILIATE_ROUTES.FORGOT_PASSWORD} replace />;
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError('');

    const password = validateAffiliatePassword(newPassword);
    const nextPasswordError = password.valid ? '' : t(password.message);
    const match = validatePasswordMatch(newPassword, confirmPassword);
    const nextConfirmError = match.valid ? '' : t(match.message);
    setNewPasswordError(nextPasswordError);
    setConfirmPasswordError(nextConfirmError);
    if (nextPasswordError || nextConfirmError) return;

    setSubmitting(true);
    try {
      await affiliateService.resetPassword({
        email,
        passwordResetToken,
        otpCode,
        newPassword,
      });
      navigate(AFFILIATE_ROUTES.PASSWORD_RESET_SUCCESS, { replace: true });
    } catch (error) {
      setFormError(
        getUserFacingErrorMessage(error, t('affiliate.forgotPassword.resetFailed')),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AffiliatePortalLayout centered>
      <div className="auth-flow__card">
        <h1 className="auth-flow__heading">{t('auth.changePassword.heading')}</h1>
        <p className="auth-flow__subheading">{t('auth.changePassword.subheading')}</p>

        <form className="auth-flow__form" onSubmit={handleSubmit} noValidate>
          <FormError
            variant="banner"
            message={formError}
            id="affiliate-reset-password-error"
          />
          <label className="auth-flow__field">
            <span>{t('auth.changePassword.newPasswordLabel')}</span>
            <PasswordInput
              className={`auth-flow__input${newPasswordError ? ' auth-flow__input--error' : ''}`}
              value={newPassword}
              onChange={event => {
                setNewPassword(event.target.value);
                if (newPasswordError) setNewPasswordError('');
              }}
              placeholder={t('auth.changePassword.newPasswordPlaceholder')}
              autoComplete="new-password"
              aria-invalid={Boolean(newPasswordError)}
              aria-describedby={
                newPasswordError ? 'affiliate-reset-new-password-error' : undefined
              }
              autoFocus
            />
            <FormError
              message={newPasswordError}
              id="affiliate-reset-new-password-error"
            />
          </label>

          <label className="auth-flow__field">
            <span>{t('auth.changePassword.confirmPasswordLabel')}</span>
            <PasswordInput
              className={`auth-flow__input${confirmPasswordError ? ' auth-flow__input--error' : ''}`}
              value={confirmPassword}
              onChange={event => {
                setConfirmPassword(event.target.value);
                if (confirmPasswordError) setConfirmPasswordError('');
              }}
              placeholder={t('auth.changePassword.confirmPasswordPlaceholder')}
              autoComplete="new-password"
              aria-invalid={Boolean(confirmPasswordError)}
              aria-describedby={
                confirmPasswordError ? 'affiliate-reset-confirm-error' : undefined
              }
            />
            <FormError
              message={confirmPasswordError}
              id="affiliate-reset-confirm-error"
            />
          </label>

          <button className="auth-flow__primary" type="submit" disabled={submitting}>
            {submitting ? t('common.loading') : t('auth.changePassword.resetPassword')}
          </button>
        </form>

        <p className="auth-flow__footer">
          <Link to={AFFILIATE_ROUTES.SIGN_IN}>
            {t('auth.changePassword.cancelBackToSignIn')}
          </Link>
        </p>
      </div>
    </AffiliatePortalLayout>
  );
}
